// Split a date range into per-week segments for the month view.
//
// The month grid renders one row per week (7 cells, Mon..Sun). A
// multi-day install that crosses a week boundary needs to render as
// multiple segments — one per week the range touches — because each
// week is its own CSS Grid container. Continuation visuals (rounded
// edges) are dropped at the join so the segments read as one bar.
//
// Input: rangeStart..rangeEnd (inclusive, YYYY-MM-DD) and a list of
// week-anchor dates (each is the Monday at the start of a week in the
// visible grid).
//
// Output: zero or more segments, each clamped to a single week.

import { toISO } from './calendar-dates.js';

export interface WeekSegmentInput {
  /** Inclusive start of the range, YYYY-MM-DD. */
  rangeStart: string;
  /** Inclusive end of the range, YYYY-MM-DD. */
  rangeEnd: string;
  /** Monday-anchored start of each week in the visible grid. */
  weekStarts: Date[];
}

export interface WeekSegment {
  /** Index into the weekStarts array — which week this segment lives in. */
  weekIndex: number;
  /** Column 0..6 within the week (0 = Mon, 6 = Sun). */
  startCol: number;
  /** Inclusive end column 0..6 within the week. */
  endCol: number;
  /** True if this segment contains the first day of the full range. */
  isFirstSegment: boolean;
  /** True if this segment contains the last day of the full range. */
  isLastSegment: boolean;
}

export function splitByWeek(input: WeekSegmentInput): WeekSegment[] {
  const { rangeStart, rangeEnd, weekStarts } = input;
  if (rangeStart > rangeEnd) return [];

  const segments: WeekSegment[] = [];
  for (let i = 0; i < weekStarts.length; i++) {
    const weekStart = weekStarts[i]!;
    const weekStartISO = toISO(weekStart);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const weekEndISO = toISO(weekEnd);

    // No overlap with this week?
    if (rangeEnd < weekStartISO) break; // ranges are sorted by start, week's after rangeEnd → done
    if (rangeStart > weekEndISO) continue;

    const segStartISO = rangeStart > weekStartISO ? rangeStart : weekStartISO;
    const segEndISO = rangeEnd < weekEndISO ? rangeEnd : weekEndISO;

    const startCol = daysBetween(weekStartISO, segStartISO);
    const endCol = daysBetween(weekStartISO, segEndISO);

    segments.push({
      weekIndex: i,
      startCol,
      endCol,
      isFirstSegment: segStartISO === rangeStart,
      isLastSegment: segEndISO === rangeEnd,
    });
  }
  return segments;
}

function daysBetween(aISO: string, bISO: string): number {
  const a = new Date(`${aISO}T00:00:00Z`).getTime();
  const b = new Date(`${bISO}T00:00:00Z`).getTime();
  return Math.round((b - a) / 86_400_000);
}
